import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  MachineSummaryRow,
  CulpritMachine,
  LineSummary,
} from "../../services/ShiftAnalysisService";

interface Props {
  machines: MachineSummaryRow[];
  culpritMachines: CulpritMachine[];
  lineSummary: LineSummary | null;
  focusedSn: string | null;
  onHover: (sn: string | null) => void;
  onPin: (sn: string) => void;
}

const STATUS_COLOR: Record<string, string> = {
  good: "#22c55e",
  watch: "#eab308",
  bad: "#ef4444",
  no_data: "#94a3b8",
};

const SIZE = 460;
const C = SIZE / 2;
const R = 182; // ring centreline radius
const TH = 15; // ring thickness
const HUB_R = 58;

/** deg 0 = top (12 o'clock), increasing clockwise. */
function polar(r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [C + r * Math.sin(a), C - r * Math.cos(a)];
}

function arcPath(r: number, a0: number, a1: number): string {
  const [x0, y0] = polar(r, a0);
  const [x1, y1] = polar(r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
}

function fmtDowntime(ms: number): string {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

const LinePulse: React.FC<Props> = ({
  machines,
  culpritMachines,
  lineSummary,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();

  // Machine arcs — sized by downtime share (with a floor so healthy machines
  // stay visible), coloured by status.
  const segments = useMemo(() => {
    const n = machines.length;
    if (n === 0) return [];
    const totalDt = machines.reduce((s, m) => s + m.downtime_ms, 0);
    const base = totalDt > 0 ? totalDt * 0.06 : 1;
    const weights = machines.map(
      (m) => (totalDt > 0 ? m.downtime_ms : 0) + base,
    );
    const wsum = weights.reduce((a, b) => a + b, 0) || 1;
    const GAP = n > 1 ? 5 : 0;
    const avail = 360 - GAP * n;
    let cur = 0;
    return machines.map((m, i) => {
      const sweep = (weights[i] / wsum) * avail;
      const a0 = cur + GAP / 2;
      const a1 = a0 + sweep;
      cur = a1 + GAP / 2;
      return { m, a0, a1, mid: (a0 + a1) / 2 };
    });
  }, [machines]);

  const midBySn = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of segments) map.set(s.m.machine_sn, s.mid);
    return map;
  }, [segments]);

  // Connectors: the top stoppers, pointing from the hub to their arc.
  const connectors = useMemo(() => {
    return culpritMachines
      .filter((c) => c.internal_stops > 0 || c.internal_seconds > 0)
      .slice(0, 3)
      .map((c) => ({ sn: c.machine_sn, mid: midBySn.get(c.machine_sn) }))
      .filter((c): c is { sn: string; mid: number } => c.mid !== undefined);
  }, [culpritMachines, midBySn]);

  const focused = focusedSn
    ? machines.find((m) => m.machine_sn === focusedSn)
    : null;

  const centerPrimary = focused
    ? `${Math.round(focused.efficiency * 100)}%`
    : lineSummary
      ? `${Math.round(lineSummary.avg_efficiency * 100)}%`
      : "—";
  const centerColor = focused
    ? (STATUS_COLOR[focused.status] ?? "#94a3b8")
    : "var(--text-primary)";

  if (machines.length === 0) {
    return (
      <div
        className="py-16 text-center text-sm"
        style={{ color: "var(--text-secondary)" }}
      >
        {t("noMachineSummary", "No machine data available for this shift.")}
      </div>
    );
  }

  return (
    <div className="relative flex justify-center">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="w-full"
        style={{ maxWidth: 460 }}
      >
        <defs>
          <radialGradient id="lp-hub" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--bg-primary)" />
            <stop offset="100%" stopColor="var(--bg-secondary)" />
          </radialGradient>
          <filter id="lp-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Decorative dotted grid */}
        {[HUB_R + 24, HUB_R + 62, R - TH - 6].map((r) => (
          <circle
            key={r}
            cx={C}
            cy={C}
            r={r}
            fill="none"
            stroke="var(--border-primary)"
            strokeWidth={1}
            strokeDasharray="1 5"
            opacity={0.5}
          />
        ))}
        {Array.from({ length: 12 }).map((_, i) => {
          const [x, y] = polar(R - TH / 2 - 4, i * 30);
          return (
            <line
              key={i}
              x1={C}
              y1={C}
              x2={x}
              y2={y}
              stroke="var(--border-primary)"
              strokeWidth={1}
              strokeDasharray="1 5"
              opacity={0.4}
            />
          );
        })}

        {/* Ring background track */}
        <circle
          cx={C}
          cy={C}
          r={R}
          fill="none"
          stroke="var(--bg-secondary)"
          strokeWidth={TH + 4}
        />

        {/* Machine arcs */}
        {segments.map(({ m, a0, a1, mid }) => {
          const color = STATUS_COLOR[m.status] ?? "#94a3b8";
          const isFocused = focusedSn === m.machine_sn;
          const dimmed = focusedSn != null && !isFocused;
          const [lx, ly] = polar(R + 16, mid);
          return (
            <g key={m.machine_sn}>
              <path
                d={arcPath(R, a0, a1)}
                fill="none"
                stroke={color}
                strokeWidth={isFocused ? TH + 7 : TH}
                strokeLinecap="round"
                opacity={dimmed ? 0.28 : 1}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s",
                  filter: isFocused ? "url(#lp-glow)" : undefined,
                }}
                onMouseEnter={() => onHover(m.machine_sn)}
                onMouseLeave={() => onHover(null)}
                onClick={() => onPin(m.machine_sn)}
              />
              {/* index label outside the arc */}
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  fill: dimmed ? "var(--text-secondary)" : color,
                  opacity: dimmed ? 0.5 : 1,
                }}
              >
                {m.machine_sn}
              </text>
            </g>
          );
        })}

        {/* Connectors: hub → biggest stoppers */}
        {connectors.map(({ sn, mid }, i) => {
          const isFocused = focusedSn === sn;
          const dimmed = focusedSn != null && !isFocused;
          const m = machines.find((x) => x.machine_sn === sn);
          const color = m ? (STATUS_COLOR[m.status] ?? "#ef4444") : "#ef4444";
          const [ex, ey] = polar(R - TH / 2 - 8, mid);
          const [sx, sy] = polar(HUB_R + 2, mid + 20);
          const [c1x, c1y] = polar(R * 0.5, mid + 28 - i * 4);
          const [c2x, c2y] = polar(R * 0.8, mid + 5);
          // Arrowhead pointing at the arc.
          const dx = ex - c2x;
          const dy = ey - c2y;
          const len = Math.hypot(dx, dy) || 1;
          const ux = dx / len;
          const uy = dy / len;
          const px = -uy;
          const py = ux;
          const tip = `${ex},${ey}`;
          const b1 = `${ex - ux * 10 + px * 5},${ey - uy * 10 + py * 5}`;
          const b2 = `${ex - ux * 10 - px * 5},${ey - uy * 10 - py * 5}`;
          return (
            <g
              key={sn}
              opacity={dimmed ? 0.25 : 1}
              style={{ transition: "opacity 0.2s" }}
            >
              <path
                d={`M ${sx} ${sy} C ${c1x} ${c1y} ${c2x} ${c2y} ${ex} ${ey}`}
                fill="none"
                stroke={color}
                strokeWidth={isFocused ? 3 : 2}
                strokeLinecap="round"
                opacity={0.85}
              />
              <polygon points={`${tip} ${b1} ${b2}`} fill={color} />
            </g>
          );
        })}

        {/* Hub */}
        <circle
          cx={C}
          cy={C}
          r={HUB_R}
          fill="url(#lp-hub)"
          stroke="var(--border-primary)"
          strokeDasharray="2 4"
        />
        <text
          x={C}
          y={C - 14}
          textAnchor="middle"
          style={{
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: "0.12em",
            fill: "var(--text-secondary)",
          }}
        >
          {focused
            ? focused.machine_sn
            : t("lineEfficiency", "LINE EFF").toUpperCase()}
        </text>
        <text
          x={C}
          y={C + 13}
          textAnchor="middle"
          style={{
            fontSize: 36,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            fill: centerColor,
          }}
        >
          {centerPrimary}
        </text>
        <text
          x={C}
          y={C + 30}
          textAnchor="middle"
          style={{ fontSize: 10, fill: "var(--text-secondary)" }}
        >
          {focused
            ? `${t("downtime", "DT")} ${fmtDowntime(focused.downtime_ms)}`
            : t("nMachines", {
                defaultValue: "{{n}} machines",
                n: machines.length,
              })}
        </text>
      </svg>
    </div>
  );
};

export default LinePulse;
